import { Loader2, Plus, Star, Trash2, Wifi } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ModelDialog } from "./ModelDialog";

interface Model {
  id: string;
  modelId: string;
  displayName: string;
  isDefault: boolean;
  connectionStatus: "connected" | "failed" | "untested";
  lastTestedAt: string | null;
}

interface ModelListProps {
  providerId: string;
}

export function ModelList({ providerId }: ModelListProps) {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`/api/providers/${providerId}/models`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch models");
      const data = await res.json();
      setModels(Array.isArray(data) ? data : (data.data ?? []));
    } catch {
      toast.error("Failed to load models");
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  async function handleTest(model: Model) {
    try {
      const res = await fetch(`/api/providers/${providerId}/models/${model.id}/test`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.status === "connected") {
        toast.success(data.message || "Model test passed");
      } else {
        toast.error(data.message || "Model test failed");
      }
      fetchModels();
    } catch {
      toast.error("Failed to test model");
    }
  }

  async function handleSetDefault(model: Model) {
    try {
      const res = await fetch(`/api/providers/${providerId}/models/${model.id}/set-default`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to set default");
      }
      toast.success("Default model updated");
      fetchModels();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to set default");
    }
  }

  async function handleDelete(model: Model) {
    if (!confirm(`Remove model "${model.displayName || model.modelId}"?`)) return;
    try {
      const res = await fetch(`/api/providers/${providerId}/models/${model.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete model");
      }
      toast.success("Model removed");
      fetchModels();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete model");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Models</span>
        <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1 h-3 w-3" />
          Add Model
        </Button>
      </div>

      {models.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No models configured yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table className="min-w-[420px] text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="font-medium">Model ID</TableHead>
                <TableHead className="font-medium">Display Name</TableHead>
                <TableHead className="font-medium">Status</TableHead>
                <TableHead className="font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {models.map((model) => (
                <ModelRow
                  key={model.id}
                  model={model}
                  onTest={() => handleTest(model)}
                  onSetDefault={() => handleSetDefault(model)}
                  onDelete={() => handleDelete(model)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ModelDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        providerId={providerId}
        onSaved={fetchModels}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: Model["connectionStatus"] }) {
  switch (status) {
    case "connected":
      return (
        <Badge
          variant="secondary"
          className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
        >
          Connected
        </Badge>
      );
    case "failed":
      return (
        <Badge
          variant="secondary"
          className="text-[10px] bg-red-500/10 text-red-600 dark:text-red-400"
        >
          Failed
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary" className="text-[10px]">
          Not tested
        </Badge>
      );
  }
}

function ModelRow({
  model,
  onTest,
  onSetDefault,
  onDelete,
}: {
  model: Model;
  onTest: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  const [testing, setTesting] = useState(false);

  async function handleTest() {
    setTesting(true);
    await onTest();
    setTesting(false);
  }

  return (
    <TableRow>
      <TableCell className="py-1.5 font-mono">
        <div className="flex items-center gap-1.5">
          {model.modelId}
          {model.isDefault && (
            <Badge
              variant="secondary"
              className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400"
            >
              Default
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="py-1.5">{model.displayName}</TableCell>
      <TableCell className="py-1.5">
        <StatusBadge status={model.connectionStatus} />
      </TableCell>
      <TableCell className="py-1.5">
        <div className="flex items-center justify-end gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wifi className="h-3 w-3" />}
            <span className="sr-only">Test model</span>
          </Button>
          {!model.isDefault && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onSetDefault}>
              <Star className="h-3 w-3" />
              <span className="sr-only">Set as default</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-3 w-3" />
            <span className="sr-only">Delete model</span>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
